import React, { useEffect, useState } from "react";
import { Container, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SnackBar from "../../components/notification/snackbar/snackbar";
import { useFetch } from "../../hooks/customFetch/useCustomFetch";
import { useAuth0 } from "@auth0/auth0-react";
import "./registration.css";
import { RegistrationHeader, PersonalInfoForm, EnterpriseForm } from "./RegistrationForms";
import WhatsAppButton from "../../components/whatsAppButton/WhatsAppButton";

const apiUrl = process.env.REACT_APP_API_URL;

interface FormData {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  recommended: string;
  address: {
    postal_code: string;
    country: string;
    street: string;
    int_number: string;
    ext_number: string;
    district: string;
    city: string;
    state: string;
    reference: string;
  };
  enterprise?: {
    social_reason: string;
    address: {
      postal_code: string;
      street: string;
      ext_number: string;
      district: string;
      state: string;
    };
  };
}

interface FormErrors {
  first_name: boolean;
  last_name: boolean;
  phone: boolean;
  email: boolean;
  postal_code: boolean;
  country: boolean;
  street: boolean;
  ext_number: boolean;
  district: boolean;
  city: boolean;
  state: boolean;
}

const Registration = () => {
  const { logout, getAccessTokenSilently, isAuthenticated } = useAuth0();

  const navigate = useNavigate();

  const { user } = useAuth0();

  const [currentStep, setCurrentStep] = useState(1);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [recommended, setRecommended] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [street, setStreet] = useState("");
  const [extNumber, setExtNumber] = useState("");
  const [intNumber, setIntNumber] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [reference, setReference] = useState("");

  const [enterpriseSocialReason, setEnterpriseSocialReason] = useState("");
  const [enterprisePostalCode, setEnterprisePostalCode] = useState("");
  const [enterpriseStreet, setEnterpriseStreet] = useState("");
  const [enterpriseExtNumber, setEnterpriseExtNumber] = useState("");
  const [enterpriseDistrict, setEnterpriseDistrict] = useState("");
  const [enterpriseState, setEnterpriseState] = useState("");
  const [enterprisePostalCodeGeo, setEnterprisePostalCodeGeo] = useState("");

  const [errors, setErrors] = useState<FormErrors>({
    first_name: false,
    last_name: false,
    phone: false,
    email: false,
    postal_code: false,
    country: false,
    street: false,
    ext_number: false,
    district: false,
    city: false,
    state: false,
  });

  const [touched, setTouched] = useState<FormErrors>({
    first_name: false,
    last_name: false,
    phone: false,
    email: false,
    postal_code: false,
    country: false,
    street: false,
    ext_number: false,
    district: false,
    city: false,
    state: false,
  });

  const [postalCodeGeo, setPostalCodeGeo] = useState("");

  const { fetchData, error, loading } = useFetch(
    "https://geocodes.envia.com/zipcode/MX/" + parseInt(postalCodeGeo),
    postalCode
  );

  const { fetchData: enterpriseFetchData } = useFetch(
    "https://geocodes.envia.com/zipcode/MX/" + parseInt(enterprisePostalCodeGeo),
    enterprisePostalCode
  );

  const validateField = (field: keyof FormErrors, value: string): boolean => {
    switch (field) {
      case "first_name":
      case "last_name":
        return value.trim().length < 2;
      case "phone":
        return !/^\d{10}$/.test(value.replace(/\s/g, ""));
      case "email":
        return !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
      case "postal_code":
        return !/^\d{5}$/.test(value);
      case "country":
      case "street":
      case "ext_number":
      case "district":
      case "city":
      case "state":
        return value.trim().length < 1;
      default:
        return false;
    }
  };

  useEffect(() => {
    setErrors({
      first_name: validateField("first_name", firstName),
      last_name: validateField("last_name", lastName),
      phone: validateField("phone", phone),
      email: validateField("email", email),
      postal_code: validateField("postal_code", postalCode),
      country: validateField("country", country),
      street: validateField("street", street),
      ext_number: validateField("ext_number", extNumber),
      district: validateField("district", district),
      city: validateField("city", city),
      state: validateField("state", state),
    });
  }, [
    firstName,
    lastName,
    phone,
    email,
    postalCode,
    country,
    street,
    extNumber,
    district,
    city,
    state,
  ]);

  const isStep1Valid =
    !errors.first_name &&
    !errors.last_name &&
    !errors.phone &&
    !errors.email &&
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    phone.trim() !== "" &&
    email.trim() !== "" &&
    recommended.trim() !== "";

  const isFormValid = isStep1Valid;

  const step1Fields = [firstName, lastName, phone, email, recommended];
  const step1FilledFields = step1Fields.filter((field) => field.trim() !== "").length;
  const step1Progress = (step1FilledFields / step1Fields.length) * 50;

  const step2Fields = [enterpriseSocialReason, enterprisePostalCode, enterpriseStreet, enterpriseExtNumber, enterpriseDistrict, enterpriseState];
  const step2FilledFields = step2Fields.filter((field) => field.trim() !== "").length;
  const step2Progress = (step2FilledFields / step2Fields.length) * 50;

  const progress = currentStep === 1 
    ? step1Progress 
    : 50 + step2Progress;

  const handleBlur = (field: keyof FormErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const onChangeZip = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPostalCodeGeo(e.target.value);
  };

  useEffect(() => {
    if (fetchData !== null && postalCodeGeo.length > 4) {
      setCity(fetchData[0]?.locality || "");
      setCountry(fetchData[0]?.country.name || "México");
      setState(fetchData[0]?.state.name || "");
      setDistrict(fetchData[0]?.suburbs[0] || "");
      setStreet(fetchData[0]?.additional_info?.street || "");
    }
  }, [fetchData, postalCodeGeo]);

  useEffect(() => {
    if (enterpriseFetchData !== null && enterprisePostalCodeGeo.length > 4) {
      setEnterpriseState(enterpriseFetchData[0]?.state.name || "");
      setEnterpriseDistrict(enterpriseFetchData[0]?.suburbs[0] || "");
      setEnterpriseStreet(enterpriseFetchData[0]?.additional_info?.street || "");
    }
  }, [enterpriseFetchData, enterprisePostalCodeGeo]);

  const onChangeEnterpriseZip = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEnterprisePostalCodeGeo(e.target.value);
  };

  const handleNextStep = () => {
    if (isStep1Valid) {
      setCurrentStep(2);
    } else {
      setMessage("Por favor, completa todos los campos requeridos correctamente");
      setOpen(true);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isFormValid) {
      setMessage(
        "Por favor, completa todos los campos requeridos correctamente"
      );
      setOpen(true);
      return;
    }

    setIsSubmitting(true);

    const formData: FormData = {
      first_name: firstName,
      last_name: lastName,
      phone,
      email: user?.email || "",
      recommended,
      address: {
        postal_code: postalCode,
        country,
        street,
        int_number: intNumber,
        ext_number: extNumber,
        district,
        city,
        state,
        reference,
      },
    };

    if (enterpriseSocialReason.trim() !== "") {
      formData.enterprise = {
        social_reason: enterpriseSocialReason,
        address: {
          postal_code: enterprisePostalCode,
          street: enterpriseStreet,
          ext_number: enterpriseExtNumber,
          district: enterpriseDistrict,
          state: enterpriseState,
        },
      };
    }

    try {
      const token = await getAccessTokenSilently();

      const response = await axios.patch(`${apiUrl}/api/v1/profile`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        window.location.href = "/";
      }
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Error al completar el registro"
      );
      setOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHelperText = (field: keyof FormErrors): string => {
    if (!touched[field] || !errors[field]) return "";

    switch (field) {
      case "first_name":
      case "last_name":
        return "Debe tener al menos 2 caracteres";
      case "phone":
        return "Debe ser un número de 10 dígitos";
      case "email":
        return "Debe ser un correo electrónico válido";
      case "postal_code":
        return "Debe ser un código postal de 5 dígitos";
      default:
        return "Este campo es requerido";
    }
  };

  return (
    <div className="registration-container">
      <Container maxWidth="sm">
        <RegistrationHeader currentStep={currentStep} progress={progress} />

        {currentStep === 1 && (
          <PersonalInfoForm
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            phone={phone}
            setPhone={setPhone}
            email={email}
            setEmail={setEmail}
            recommended={recommended}
            setRecommended={setRecommended}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            getHelperText={getHelperText}
            isStep1Valid={isStep1Valid}
            handleNextStep={handleNextStep}
          />
        )}

        {currentStep === 2 && (
          <EnterpriseForm
            enterpriseSocialReason={enterpriseSocialReason}
            setEnterpriseSocialReason={setEnterpriseSocialReason}
            enterprisePostalCode={enterprisePostalCode}
            setEnterprisePostalCode={setEnterprisePostalCode}
            enterpriseStreet={enterpriseStreet}
            setEnterpriseStreet={setEnterpriseStreet}
            enterpriseExtNumber={enterpriseExtNumber}
            setEnterpriseExtNumber={setEnterpriseExtNumber}
            enterpriseDistrict={enterpriseDistrict}
            setEnterpriseDistrict={setEnterpriseDistrict}
            enterpriseState={enterpriseState}
            setEnterpriseState={setEnterpriseState}
            onChangeEnterpriseZip={onChangeEnterpriseZip}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmit}
            handlePrevStep={handlePrevStep}
          />
        )}

        <SnackBar
          onClose={() => setOpen(false)}
          open={open}
          message={message}
          severity={message.includes("exitosamente") ? "success" : "error"}
        />
        <WhatsAppButton/>
      </Container>
    </div>
  );
};

export default Registration;
